import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, Calendar, Clock, MapPin, User } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { useEvent } from '@/hooks/use-api'
import { useRecordBreadcrumb } from '@/contexts/BreadcrumbContext'
import { CategoryBadge } from '@/components/ui/CategoryBadge'
import { toDisplayDate } from '@/lib/utils'
import { explainError } from '@/lib/explain-error'

export default function EventDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const { data: event, isLoading, error } = useEvent(id!)
  useRecordBreadcrumb(event?.subject)

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96" role="status" aria-live="polite">
        <div className="text-center">
          <div
            className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-current border-r-transparent motion-reduce:animate-[spin_1.5s_linear_infinite]"
            role="progressbar"
            aria-label="Loading event details"
          ></div>
          <p className="mt-2 text-sm text-muted-foreground">Loading event details...</p>
        </div>
      </div>
    )
  }

  if (error || !event) {
    return (
      <div className="flex flex-col items-center justify-center h-96" role="alert">
        <p className="text-lg font-semibold text-destructive">Error loading event</p>
        <p className="text-sm text-muted-foreground mt-2">
          {explainError(error, 'Event not found')}
        </p>
        <Button
          onClick={() => navigate('/events')}
          className="mt-4"
          aria-label="Return to events list"
        >
          <ArrowLeft className="mr-2 h-4 w-4" aria-hidden="true" />
          Back to Events
        </Button>
      </div>
    )
  }

  const formatDateTime = (date?: string) => {
    if (!date) return 'N/A'
    return (toDisplayDate(date) ?? new Date(date)).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    })
  }

  const getTypeBadgeVariant = (type?: string) => {
    const lowerType = type?.toLowerCase() || ''
    if (lowerType === 'meeting') return 'default'
    if (lowerType === 'call') return 'secondary'
    if (lowerType === 'email') return 'outline'
    return 'default'
  }

  return (
    <div className="space-y-6" role="main" aria-label="Event details">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/events')}
            aria-label="Back to events list"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          </Button>
          <CategoryBadge type="event" />
          <div>
            <h1 className="text-3xl font-bold tracking-tight">
              {event.subject || 'Untitled Event'}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              {formatDateTime(event.start_date_time)}
              {event.location && ` - ${event.location}`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {event.type && <Badge variant={getTypeBadgeVariant(event.type)}>{event.type}</Badge>}
          {event.is_all_day_event && <Badge variant="secondary">All Day</Badge>}
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0">
            <CardTitle className="text-sm font-medium">Start Time</CardTitle>
            <Calendar className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
          </CardHeader>
          <CardContent className="p-3">
            <div className="text-sm font-medium">{formatDateTime(event.start_date_time)}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0">
            <CardTitle className="text-sm font-medium">End Time</CardTitle>
            <Clock className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
          </CardHeader>
          <CardContent className="p-3">
            <div className="text-sm font-medium">{formatDateTime(event.end_date_time)}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0">
            <CardTitle className="text-sm font-medium">Location</CardTitle>
            <MapPin className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
          </CardHeader>
          <CardContent className="p-3">
            <div className="text-sm font-medium truncate">{event.location || 'N/A'}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0">
            <CardTitle className="text-sm font-medium">Owner</CardTitle>
            <User className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
          </CardHeader>
          <CardContent className="p-3">
            <div className="text-sm font-medium truncate">{event.owner_name || 'Unassigned'}</div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Event Information */}
        <Card>
          <CardHeader>
            <CardTitle>Event Information</CardTitle>
            <CardDescription>Event details and scheduling</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-0">
              <div className="py-2 border-b border-border">
                <Label className="text-sm font-medium text-muted-foreground">Subject</Label>
                <div className="text-sm">{event.subject || 'N/A'}</div>
              </div>
              <div className="py-2 border-b border-border">
                <Label className="text-sm font-medium text-muted-foreground">Type</Label>
                <div className="text-sm">{event.type || 'N/A'}</div>
              </div>
              <div className="py-2 border-b border-border">
                <Label className="text-sm font-medium text-muted-foreground">Start Date/Time</Label>
                <div className="text-sm">{formatDateTime(event.start_date_time)}</div>
              </div>
              <div className="py-2 border-b border-border">
                <Label className="text-sm font-medium text-muted-foreground">End Date/Time</Label>
                <div className="text-sm">{formatDateTime(event.end_date_time)}</div>
              </div>
              <div className="py-2 border-b border-border">
                <Label className="text-sm font-medium text-muted-foreground">Location</Label>
                <div className="text-sm">{event.location || 'N/A'}</div>
              </div>
              <div className="py-2 border-b border-border">
                <Label className="text-sm font-medium text-muted-foreground">All Day Event</Label>
                <div className="text-sm">{event.is_all_day_event ? 'Yes' : 'No'}</div>
              </div>
              <div className="py-2 border-b border-border">
                <Label className="text-sm font-medium text-muted-foreground">Show As</Label>
                <div className="text-sm">{event.show_as || 'N/A'}</div>
              </div>
              <div className="py-2 border-b border-border">
                <Label className="text-sm font-medium text-muted-foreground">Private</Label>
                <div className="text-sm">{event.is_private ? 'Yes' : 'No'}</div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Related Information */}
        <Card>
          <CardHeader>
            <CardTitle>Related Information</CardTitle>
            <CardDescription>Related records and attendees</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-0">
              <div className="py-2 border-b border-border">
                <Label className="text-sm font-medium text-muted-foreground">Related To</Label>
                <div className="text-sm">
                  {event.what_name || event.related_to_name || 'N/A'}
                  {event.what_type && ` (${event.what_type})`}
                </div>
              </div>
              <div className="py-2 border-b border-border">
                <Label className="text-sm font-medium text-muted-foreground">Contact/Lead</Label>
                <div className="text-sm">
                  {event.who_name || 'N/A'}
                  {event.who_type && ` (${event.who_type})`}
                </div>
              </div>
              <div className="py-2 border-b border-border">
                <Label className="text-sm font-medium text-muted-foreground">Owner</Label>
                <div className="text-sm">{event.owner_name || 'Unassigned'}</div>
              </div>
              <div className="py-2 border-b border-border">
                <Label className="text-sm font-medium text-muted-foreground">Reminder</Label>
                <div className="text-sm">
                  {event.is_reminder_set
                    ? formatDateTime(event.reminder_date_time)
                    : 'No reminder set'}
                </div>
              </div>
              <div className="md:col-span-2 py-2 border-b border-border">
                <Label className="text-sm font-medium text-muted-foreground">Description</Label>
                <div className="text-sm whitespace-pre-wrap">{event.description || 'N/A'}</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
